import { useSyncExternalStore } from 'react'

export type CandidatesExportProgressStatus = 'idle' | 'running' | 'success' | 'error'

export type CandidatesExportProgressState = {
  status: CandidatesExportProgressStatus
  title: string
  subtitle: string
  progress: number
}

const idleState: CandidatesExportProgressState = {
  status: 'idle',
  title: '',
  subtitle: '',
  progress: 0,
}

let state: CandidatesExportProgressState = idleState
const listeners = new Set<() => void>()

function emit() {
  for (const listener of listeners) listener()
}

function setState(patch: Partial<CandidatesExportProgressState>) {
  state = { ...state, ...patch }
  emit()
}

export function getCandidatesExportProgressState(): CandidatesExportProgressState {
  return state
}

export function subscribeCandidatesExportProgress(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useCandidatesExportProgress(): CandidatesExportProgressState {
  return useSyncExternalStore(
    subscribeCandidatesExportProgress,
    getCandidatesExportProgressState,
    getCandidatesExportProgressState,
  )
}

export function startCandidatesExportProgress(title: string, total: number): void {
  setState({
    status: 'running',
    title,
    subtitle: total > 0 ? `Preparing… 0/${total}` : 'Preparing export…',
    progress: 0,
  })
}

export function updateCandidatesExportProgress(input: {
  subtitle: string
  processed: number
  total: number
  listWeight?: number
}): void {
  if (state.status !== 'running') return
  const safeTotal = input.total <= 0 ? 1 : input.total
  const listWeight = input.listWeight ?? 0.2
  const value = (input.processed / safeTotal) * listWeight
  setState({
    subtitle: input.subtitle,
    progress: Math.min(1, Math.max(0, value)),
  })
}

export function updateCandidatesExportProgressCombined(input: {
  subtitle: string
  progress: number
}): void {
  if (state.status !== 'running') return
  setState({
    subtitle: input.subtitle,
    progress: Math.min(1, Math.max(0, input.progress)),
  })
}

export function completeCandidatesExportProgress(subtitle: string): void {
  setState({
    status: 'success',
    subtitle,
    progress: 1,
  })
}

export function failCandidatesExportProgress(subtitle: string): void {
  setState({
    status: 'error',
    subtitle,
    progress: state.progress,
  })
}

export function dismissCandidatesExportProgress(): void {
  state = idleState
  emit()
}

export function isCandidatesExportProgressRunning(): boolean {
  return state.status === 'running'
}
